import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useInView } from "react-intersection-observer";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CreatePost } from "@/components/Community/CreatePost";
import { Post, PostData } from "@/components/Community/Post";
import { PostSkeleton } from "@/components/Community/PostSkeleton";
import { EventsPage } from "@/components/Community/EventsPage";
import { LatestJobs } from "@/components/Community/LatestJobs";
import { Card } from "@/components/ui/card";
import { Rss, FileText, Calendar, Users, Briefcase, Headphones } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AppState } from "@/store";
import { extractHashtags } from "@/utils/community";
import {
  getPosts,
  getUserPosts,
  updatePost,
  deletePost,
  reactPost,
  removeReactPost,
  createComment,
  updateComment,
  deleteComment,
  sharePost,
  registerEvent,
  unregisterEvent,
  cancelEvent as cancelEventAPI,
  getMyReportsStats,
  getPostById,
  Post as APIPost,
  Comment as APIComment,
} from "@/api/community";
import { useToast } from "@/components/ui/use-toast";
import { usePosthog } from "@/hooks/usePosthog";
import { SupportPage } from "@/components/Community/SupportPage";

// Sample posts data

export function Community() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { trackEvent } = usePosthog();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get initial section from URL query params, default to 'feed'
  const initialSection = searchParams.get("section") || "feed";

  const [activeSection, setActiveSection] = useState(initialSection);
  const [posts, setPosts] = useState<PostData[]>([]);
  const [userPosts, setUserPosts] = useState<PostData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isLoadingUserPosts, setIsLoadingUserPosts] = useState(false);
  const [isInitialLoadUserPosts, setIsInitialLoadUserPosts] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0); // Used to force refresh Events and Jobs sections
  const isFirstRender = useRef(true); // Track first render to avoid unnecessary refresh

  // Infinite scroll observer
  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });

  const userData = useSelector((state: AppState) => state.auth.userData);
  const user = userData?.user;
  const [supportReportCount, setSupportReportCount] = useState(0);

  // Fetch support notification count on mount
  useEffect(() => {
    const fetchSupportStats = async () => {
      try {
        const stats = await getMyReportsStats();
        setSupportReportCount(stats.by_status?.pending || 0);
      } catch (error) {
        console.error("Error fetching support stats:", error);
      }
    };
    fetchSupportStats();
  }, []);

  const sidebarNavItems = [
    { id: "feed", label: t("community.post_details.nav_feed"), icon: Rss },
    { id: "my-posts", label: t("community.post_details.nav_my_posts"), icon: FileText },
    { id: "events", label: t("community.post_details.nav_events"), icon: Calendar },
    {
      id: "support",
      label: t("community.post_details.nav_support"),
      icon: Headphones,
      badge: supportReportCount,
    },
    { id: "latest-jobs", label: t("community.post_details.nav_latest_jobs"), icon: Briefcase },
  ].filter((item) => {
    // Hide Latest Jobs for employer users
    if (item.id === "latest-jobs" && user?.type === "employer") {
      return false;
    }
    return true;
  });

  // Update URL when section changes
  const handleSectionChange = (section: string) => {
    trackEvent("community_section_changed", {
      from_section: activeSection,
      to_section: section,
    });

    setActiveSection(section);
    navigate(`?section=${section}`, { replace: true });
  };

  // Sync state with URL on mount and when URL changes
  useEffect(() => {
    const section = searchParams.get("section") || "feed";
    if (section !== activeSection) {
      setActiveSection(section);
    }
  }, [searchParams]);

  // Scroll to top when section changes (for within-page navigation)
  useEffect(() => {
    // Always scroll on section change, even on initial load from URL params
    const scrollToTop = () => {
      let scrolled = false;

      // 1. Try to scroll the main layout container (most reliable)
      const layoutContainer = document
        .querySelector("main[data-community-container]")
        ?.closest(".overflow-y-auto, .overflow-auto");
      if (layoutContainer instanceof HTMLElement) {
        layoutContainer.scrollTop = 0;
        scrolled = true;
      }

      // 2. Scroll all overflow containers
      const scrollContainers = document.querySelectorAll(
        ".overflow-y-auto, .overflow-auto, [data-scroll-container]",
      );
      scrollContainers.forEach((container) => {
        if (container instanceof HTMLElement) {
          container.scrollTop = 0;
          scrolled = true;
        }
      });

      // 3. Fallback to window scroll
      window.scrollTo({ top: 0, behavior: "instant" });
    };

    // Scroll immediately
    scrollToTop();

    // Also scroll after delays to catch lazy-loaded content
    const timeoutId1 = setTimeout(scrollToTop, 50);
    const timeoutId2 = setTimeout(scrollToTop, 150);

    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
    };
  }, [activeSection]);

  // Helper function to transform API comments to PostData format
  const transformCommentsToPostFormat = (apiComments: any[]): any[] => {
    return apiComments.map((comment) => ({
      id: comment.id || comment._id,
      author: {
        id: comment.author?.id || comment.author_id,
        name: comment.author?.full_name || t("community.feed.unknown_user"),
        avatar: comment.author?.photo_thumbnail_url || comment.author?.photo || "",
        title: comment.author?.job_title
          ? comment.author.job_title
          : comment.author?.type === "job_seeker"
            ? t("community.feed.jobseeker")
            : comment.author?.type === "employer"
              ? t("community.feed.employer")
              : "",
        type: comment.author?.type,
        organizationId: comment.author?.organization_id,
      },
      content: comment.content,
      timestamp: new Date(comment.created_at),
      isEdited: Array.isArray(comment.edit_history) && comment.edit_history.length > 0,
      editedAt: comment.edit_history?.[0]?.edited_at
        ? new Date(comment.edit_history[0].edited_at)
        : undefined,
      likes: [],
      replies: comment.replies ? transformCommentsToPostFormat(comment.replies) : [],
    }));
  };

  // Fetch posts on mount
  useEffect(() => {
    if (isInitialLoad) {
      trackEvent("community_page_viewed", {
        initial_section: initialSection,
      });
      fetchPostsData(1, true, true);
    }
  }, []);

  // Load more posts when user scrolls to bottom
  useEffect(() => {
    if (inView && hasMore && !isLoading && !isLoadingMore && activeSection === "feed") {
      trackEvent("community_load_more", {
        page: currentPage + 1,
        section: activeSection,
      });
      fetchPostsData(currentPage + 1, false);
    }
  }, [inView, hasMore, isLoading, isLoadingMore, currentPage, activeSection]);

  const fetchPostsData = async (page = 1, isInitial = false, showSkeleton = false) => {
    try {
      if (showSkeleton) {
        setIsLoading(true);
      } else if (isInitial) {
        // Refetching from page 1 but not initial load
        setIsLoadingMore(false);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);

      const data = await getPosts({ page, limit: 10 });

      // Update pagination state
      setTotalPages(data.pagination.pages);
      setHasMore(page < data.pagination.pages);
      setCurrentPage(page);

      // Transform API posts to PostData format
      // Comments are lazy-loaded by the Post component when user clicks "show comments"
      const transformedPosts = data.posts.map((apiPost) => {
        // Transform shared_post or original_post to flat structure if it exists
        // Use original_post for posts with type='shared', otherwise use shared_post
        let transformedSharedPost = undefined;
        const sourcePost = apiPost.type === "shared" ? apiPost.original_post : apiPost.shared_post;

        if (sourcePost && sourcePost.author) {
          transformedSharedPost = {
            full_name: sourcePost.author.full_name,
            email: sourcePost.author.email,
            type: sourcePost.author.type,
            photo_thumbnail_url: sourcePost.author.photo_thumbnail_url,
            job_title: sourcePost.author.job_title,
            id: sourcePost.id || sourcePost.author.id, // Use post ID, fallback to author ID
            author_id: sourcePost.author.id, // Original event post author ID
            content: sourcePost.content,
            media_urls: sourcePost.media_urls,
            tagged_users: sourcePost.tagged_users,
            deleted: sourcePost.deleted,
            deleted_at: sourcePost.deleted_at ? new Date(sourcePost.deleted_at) : undefined,
            // Include event details for shared event posts
            isEvent: sourcePost.is_event || sourcePost.type === "event",
            eventDetails:
              (sourcePost.is_event || sourcePost.type === "event") && sourcePost.event_details
                ? {
                    title: sourcePost.event_details.title,
                    date: new Date(sourcePost.event_details.date),
                    endDate: sourcePost.event_details.end_date
                      ? new Date(sourcePost.event_details.end_date)
                      : undefined,
                    location: sourcePost.event_details.location,
                    maxParticipants: sourcePost.event_details.max_participants,
                    description: sourcePost.event_details.description,
                  }
                : undefined,
            eventStatus: sourcePost.event_status as "active" | "cancelled" | undefined,
            eventParticipants: sourcePost.event_participants
              ? sourcePost.event_participants
                  .filter(
                    (participant: any) =>
                      participant?.user !== null && participant?.user !== undefined,
                  )
                  .map((participant: any) => ({
                    userId: participant.user.id,
                    userName: participant.user.full_name,
                    userTitle:
                      participant.user.job_title ||
                      (participant.user.type === "job_seeker"
                        ? t("community.feed.job_seeker")
                        : t("community.feed.employer")),
                    userType: participant.user.type,
                    organizationId: participant.user.organization_id,
                    photoUrl: participant.user.photo_thumbnail_url || participant.user.photo,
                    registeredAt: new Date(participant.registered_at),
                  }))
              : [],
          };
        }

        return {
          ...apiPost,
          timestamp: new Date(apiPost.created_at),
          isEdited: apiPost.edit_history && apiPost.edit_history.length > 0,
          editedAt: apiPost.edit_history?.[0]?.edited_at
            ? new Date(apiPost.edit_history[0].edited_at)
            : undefined,
          likes: [],
          comments: [],
          // Handle shared posts
          isShared: apiPost.type === "shared_post" || apiPost.type === "shared",
          originalPostId: apiPost.shared_post_id,
          sharedAt: apiPost.type === "shared_post" ? new Date(apiPost.created_at) : undefined,
          shared_post: transformedSharedPost,
          // Handle event posts
          isEvent: apiPost.type === "event",
          eventDetails:
            apiPost.type === "event" && apiPost.event_details
              ? {
                  title: apiPost.event_details.title,
                  date: new Date(apiPost.event_details.date),
                  endDate: apiPost.event_details.end_date
                    ? new Date(apiPost.event_details.end_date)
                    : undefined,
                  location: apiPost.event_details.location,
                  maxParticipants: apiPost.event_details.max_participants,
                  description: apiPost.event_details.description,
                }
              : undefined,
          eventStatus: apiPost.event_status as "active" | "cancelled" | undefined,
          eventParticipants: apiPost.event_participants
            ? apiPost.event_participants
                .filter(
                  (participant: any) =>
                    participant?.user !== null && participant?.user !== undefined,
                )
                .map((participant: any) => ({
                  userId: participant.user.id,
                  userName: participant.user.full_name,
                  userTitle:
                    participant.user.job_title ||
                    (participant.user.type === "job_seeker"
                      ? t("community.feed.job_seeker")
                      : t("community.feed.employer")),
                  userType: participant.user.type,
                  organizationId: participant.user.organization_id,
                  photoUrl: participant.user.photo_thumbnail_url || participant.user.photo,
                  registeredAt: new Date(participant.registered_at),
                }))
            : [],
        };
      });

      // If initial load or refresh, replace posts; otherwise append
      if (isInitial || page === 1) {
        setPosts(transformedPosts);
      } else {
        setPosts((prevPosts) => [...prevPosts, ...transformedPosts]);
      }

      setIsInitialLoad(false);
    } catch (err) {
      console.error("Error fetching posts:", err);
      setError(t("community.feed.failed_load_posts"));
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const fetchUserPostsData = async (showSkeleton = false) => {
    if (!user?.id) {
      toast({
        title: t("community.feed.user_id_not_found"),
        variant: "destructive",
      });
      return;
    }

    try {
      if (showSkeleton) {
        setIsLoadingUserPosts(true);
      }
      const data = await getUserPosts(user.id, { page: 1, limit: 20 });

      // Transform API posts to PostData format
      // Comments are lazy-loaded by the Post component when user clicks "show comments"
      const transformedPosts = data.posts.map((apiPost) => {
        // Transform shared_post or original_post to flat structure if it exists
        // Use original_post for posts with type='shared', otherwise use shared_post
        let transformedSharedPost = undefined;
        const sourcePost = apiPost.type === "shared" ? apiPost.original_post : apiPost.shared_post;

        if (sourcePost && sourcePost.author) {
          transformedSharedPost = {
            full_name: sourcePost.author.full_name,
            email: sourcePost.author.email,
            type: sourcePost.author.type,
            photo_thumbnail_url: sourcePost.author.photo_thumbnail_url,
            job_title: sourcePost.author.job_title,
            id: sourcePost.id || sourcePost.author.id, // Use post ID, fallback to author ID
            author_id: sourcePost.author.id, // Original event post author ID
            content: sourcePost.content,
            media_urls: sourcePost.media_urls,
            tagged_users: sourcePost.tagged_users,
            deleted: sourcePost.deleted,
            deleted_at: sourcePost.deleted_at ? new Date(sourcePost.deleted_at) : undefined,
            // Include event details for shared event posts
            isEvent: sourcePost.is_event || sourcePost.type === "event",
            eventDetails:
              (sourcePost.is_event || sourcePost.type === "event") && sourcePost.event_details
                ? {
                    title: sourcePost.event_details.title,
                    date: new Date(sourcePost.event_details.date),
                    endDate: sourcePost.event_details.end_date
                      ? new Date(sourcePost.event_details.end_date)
                      : undefined,
                    location: sourcePost.event_details.location,
                    maxParticipants: sourcePost.event_details.max_participants,
                    description: sourcePost.event_details.description,
                  }
                : undefined,
            eventStatus: sourcePost.event_status as "active" | "cancelled" | undefined,
            eventParticipants: sourcePost.event_participants
              ? sourcePost.event_participants
                  .filter(
                    (participant: any) =>
                      participant?.user !== null && participant?.user !== undefined,
                  )
                  .map((participant: any) => ({
                    userId: participant.user.id,
                    userName: participant.user.full_name,
                    userTitle:
                      participant.user.job_title ||
                      (participant.user.type === "job_seeker"
                        ? t("community.feed.job_seeker")
                        : t("community.feed.employer")),
                    userType: participant.user.type,
                    organizationId: participant.user.organization_id,
                    photoUrl: participant.user.photo_thumbnail_url || participant.user.photo,
                    registeredAt: new Date(participant.registered_at),
                  }))
              : [],
          };
        }

        return {
          ...apiPost,
          timestamp: new Date(apiPost.created_at),
          isEdited: apiPost.edit_history && apiPost.edit_history.length > 0,
          editedAt: apiPost.edit_history?.[0]?.edited_at
            ? new Date(apiPost.edit_history[0].edited_at)
            : undefined,
          likes: [],
          comments: [],
          // Handle shared posts
          isShared: apiPost.type === "shared_post" || apiPost.type === "shared",
          originalPostId: apiPost.shared_post_id,
          sharedAt: apiPost.type === "shared_post" ? new Date(apiPost.created_at) : undefined,
          shared_post: transformedSharedPost,
          // Handle event posts
          isEvent: apiPost.type === "event",
          eventDetails:
            apiPost.type === "event" && apiPost.event_details
              ? {
                  title: apiPost.event_details.title,
                  date: new Date(apiPost.event_details.date),
                  endDate: apiPost.event_details.end_date
                    ? new Date(apiPost.event_details.end_date)
                    : undefined,
                  location: apiPost.event_details.location,
                  maxParticipants: apiPost.event_details.max_participants,
                  description: apiPost.event_details.description,
                }
              : undefined,
          eventStatus: apiPost.event_status as "active" | "cancelled" | undefined,
          eventParticipants: apiPost.event_participants
            ? apiPost.event_participants
                .filter(
                  (participant: any) =>
                    participant?.user !== null && participant?.user !== undefined,
                )
                .map((participant: any) => ({
                  userId: participant.user.id,
                  userName: participant.user.full_name,
                  userTitle:
                    participant.user.job_title ||
                    (participant.user.type === "job_seeker"
                      ? t("community.feed.job_seeker")
                      : t("community.feed.employer")),
                  userType: participant.user.type,
                  organizationId: participant.user.organization_id,
                  photoUrl: participant.user.photo_thumbnail_url || participant.user.photo,
                  registeredAt: new Date(participant.registered_at),
                }))
            : [],
        };
      });

      setUserPosts(transformedPosts);
      setIsInitialLoadUserPosts(false);
    } catch (err) {
      console.error("Error fetching user posts:", err);
      toast({
        title: t("community.feed.failed_load_your_posts"),
        variant: "destructive",
      });
    } finally {
      if (showSkeleton) {
        setIsLoadingUserPosts(false);
      }
    }
  };

  // Refresh data when switching tabs/sections
  useEffect(() => {
    // Skip refresh on first render (initial mount)
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (activeSection === "my-posts") {
      // Always refresh user posts when switching to my-posts
      fetchUserPostsData(isInitialLoadUserPosts); // Show skeleton only on first load
    } else if (activeSection === "feed") {
      // Refresh feed when switching back to feed
      setCurrentPage(1);
      setHasMore(true);
      fetchPostsData(1, true, false); // Don't show skeleton when switching tabs
    } else if (activeSection === "events" || activeSection === "latest-jobs") {
      // Increment refresh key to force remount and refresh data
      setRefreshKey((prev) => prev + 1);
    }
  }, [activeSection]);

  const handlePostCreated = async () => {
    // Refresh posts from API to get the latest data (without showing skeleton)
    if (activeSection === "my-posts") {
      await fetchUserPostsData(false); // Don't show skeleton when adding new post
    } else {
      // Reset to first page when creating a new post
      setCurrentPage(1);
      setHasMore(true);
      await fetchPostsData(1, true, false); // Don't show skeleton when adding new post
    }
  };

  const handleLike = async (postId: string, emoji: string) => {
    if (!user?.id) return;

    const post = posts.find((p) => p.id === postId);
    if (!post) return;

    const existingLike = post.likes?.find((like) => like.userId === user.id);

    try {
      if (existingLike && existingLike.emoji === emoji) {
        // Same emoji clicked - unlike the post
        await removeReactPost(postId);
        // Update local state optimistically
        setPosts(
          posts.map((p) => {
            if (p.id === postId) {
              const newLikes = p.likes?.filter((like) => like.userId !== user.id) || [];
              return {
                ...p,
                likes: newLikes,
                likes_count: Math.max(0, p.likes_count - 1),
              };
            }
            return p;
          }),
        );
      } else {
        // Different emoji or new like
        await reactPost(postId, { emoji });
        // Update local state optimistically
        setPosts(
          posts.map((p) => {
            if (p.id === postId) {
              const newLikes = p.likes?.filter((like) => like.userId !== user.id) || [];
              const isNewReaction = !existingLike;
              return {
                ...p,
                likes: [
                  ...newLikes,
                  {
                    userId: user.id,
                    userName: user.full_name || t("community.feed.unknown_user"),
                    emoji,
                  },
                ],
                likes_count: isNewReaction ? p.likes_count + 1 : p.likes_count,
              };
            }
            return p;
          }),
        );
      }
    } catch (error) {
      console.error("Error updating reaction:", error);
      toast({
        title: t("community.post_details.failed_reaction"),
        description: t("community.post_details.failed_reaction_desc"),
        variant: "destructive",
      });
    }
  };

  const handleComment = async (
    postId: string,
    comment: string,
    mentionedUserIds: string[] = [],
  ) => {
    try {
      const newComment = await createComment({
        post_id: postId,
        content: comment,
        mentioned_users: mentionedUserIds,
      });

      // Transform the API comment to UI format
      const transformedComment = transformCommentsToPostFormat([newComment])[0];

      // Update local state with the new comment
      setPosts(
        posts.map((post) => {
          if (post.id === postId) {
            return {
              ...post,
              comments: [...(post.comments || []), transformedComment],
              comments_count: post.comments_count + 1,
            };
          }
          return post;
        }),
      );

      toast({
        title: t("community.feed.comment_added"),
        description: t("community.feed.comment_added_desc"),
      });
    } catch (error) {
      console.error("Error creating comment:", error);
      toast({
        title: t("community.feed.failed_add_comment"),
        description: t("community.feed.failed_add_comment_desc"),
        variant: "destructive",
      });
    }
  };

  const handleShare = async (postId: string) => {
    if (!user?.id) return;

    try {
      // Call the API to share the post
      await sharePost(postId, {
        share_content: "", // You can add UI to allow users to add custom share content
      });

      // Refresh posts to get the updated data
      setCurrentPage(1);
      setHasMore(true);
      await fetchPostsData(1, true);

      toast({
        title: t("community.feed.post_shared"),
        description: t("community.feed.post_shared_desc"),
      });
    } catch (error) {
      console.error("Error sharing post:", error);
      toast({
        title: t("community.feed.failed_share_post"),
        description: t("community.feed.failed_share_post_desc"),
        variant: "destructive",
      });
    }
  };

  const handleEdit = async (
    postId: string,
    updates: {
      content: string;
      media_urls: string[];
      tagged_users: string[];
      eventDetails?: {
        title: string;
        date: Date;
        endDate?: Date;
        location: string;
        maxParticipants?: number;
        description?: string;
      };
    },
  ) => {
    try {
      // Extract hashtags from the new content
      const hashtags = extractHashtags(updates.content);

      const payload: any = {
        content: updates.content,
        visibility: "public",
        hashtag_names: hashtags,
        media_urls: updates.media_urls,
        tagged_users: updates.tagged_users,
      };

      // Event posts edit their details and their body in a single request, so the
      // unified edit surface saves everything in one go.
      if (updates.eventDetails) {
        // Look in both lists: the My Posts tab renders from `userPosts`, which is a
        // separate fetch from the paginated feed. Searching only `posts` returned
        // undefined there and sent `event_status: 'active'`, reviving cancelled events.
        const existing =
          posts.find((p) => p.id === postId) ?? userPosts.find((p) => p.id === postId);
        payload.event_status = existing?.eventStatus === "cancelled" ? "cancelled" : "active";
        payload.event_details = {
          title: updates.eventDetails.title,
          date: updates.eventDetails.date.toISOString(),
          location: updates.eventDetails.location,
          ...(updates.eventDetails.endDate && {
            end_date: updates.eventDetails.endDate.toISOString(),
          }),
          ...(updates.eventDetails.maxParticipants && {
            max_participants: updates.eventDetails.maxParticipants,
          }),
          ...(updates.eventDetails.description && {
            description: updates.eventDetails.description,
          }),
        };
      }

      // Call the API to update the post
      const updatedPost = await updatePost(postId, payload);

      // Update the local state with the API response
      const updatePostData = (post: PostData) => {
        if (post.id === postId) {
          return {
            ...post,
            content: updatedPost.content,
            media_urls: updatedPost.media_urls,
            "media_urls:raw": updatedPost["media_urls:raw"],
            tagged_users: updatedPost.tagged_users,
            hashtags: updatedPost.hashtags,
            ...(updates.eventDetails && { eventDetails: updates.eventDetails }),
            isEdited: true,
            editedAt: new Date(updatedPost.updated_at),
            edit_history: updatedPost.edit_history,
            updated_at: updatedPost.updated_at,
          };
        }
        return post;
      };

      setPosts(posts.map(updatePostData));
      setUserPosts(userPosts.map(updatePostData));

      toast({
        title: t("community.feed.post_updated"),
        description: t("community.feed.post_updated_desc"),
      });
    } catch (error: any) {
      console.error("Error updating post:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      console.error("Error config:", error.config);

      toast({
        title: t("community.feed.failed_update_post"),
        description: error.response?.data?.message || t("community.feed.failed_update_post_desc"),
        variant: "destructive",
      });
      // Rethrow so the Post edit surface stays open with the user's changes.
      throw error;
    }
  };

  const handleDelete = async (
    postId: string,
    reason?: string,
    postType?: string,
    isEvent?: boolean,
  ) => {
    try {
      // Find the post to check if it's an event (fallback if not passed as parameter

      if (isEvent) {
        // For event posts, use updatePost API to set visibility to private
        await updatePost(postId, {
          content: "",
          visibility: "private",
        });

        toast({
          title: t("community.feed.event_post_deleted"),
          description: t("community.feed.event_post_deleted_desc"),
        });
      } else {
        // For regular posts, use deletePost API
        const deleteReason = reason || "Post removed by user";
        await deletePost(postId, { reason: deleteReason });

        toast({
          title: t("community.feed.post_deleted"),
          description: t("community.feed.post_deleted_desc"),
        });
      }

      // Update both posts and userPosts states to remove the deleted post
      setPosts(posts.filter((post) => post.id !== postId));
      setUserPosts(userPosts.filter((post) => post.id !== postId));
    } catch (error) {
      console.error("Error deleting post:", error);
      toast({
        title: t("community.feed.failed_delete_post"),
        description: t("community.feed.failed_delete_post_desc"),
        variant: "destructive",
      });
    }
  };

  const handleEditComment = async (
    postId: string,
    commentId: string,
    newContent: string,
    mentionedUserIds?: string[],
  ) => {
    try {
      await updateComment(commentId, { content: newContent, mentioned_users: mentionedUserIds });

      // Update local state optimistically
      setPosts(
        posts.map((post) => {
          if (post.id === postId) {
            return {
              ...post,
              comments: post.comments?.map((comment) =>
                comment.id === commentId
                  ? { ...comment, content: newContent, isEdited: true, editedAt: new Date() }
                  : comment,
              ),
            };
          }
          return post;
        }),
      );

      toast({
        title: t("community.feed.comment_updated"),
        description: t("community.feed.comment_updated_desc"),
      });
    } catch (error) {
      console.error("Error updating comment:", error);
      toast({
        title: t("community.feed.failed_update_comment"),
        description: t("community.feed.failed_update_comment_desc"),
        variant: "destructive",
      });
    }
  };

  const handleDeleteComment = async (postId: string, commentId: string) => {
    try {
      await deleteComment(commentId, "Deleted by user");

      // Update local state optimistically
      setPosts(
        posts.map((post) => {
          if (post.id === postId) {
            return {
              ...post,
              comments: post.comments?.filter((comment) => comment.id !== commentId),
              comments_count: Math.max(0, post.comments_count - 1),
            };
          }
          return post;
        }),
      );

      toast({
        title: t("community.feed.comment_deleted"),
        description: t("community.feed.comment_deleted_desc"),
      });
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast({
        title: t("community.feed.failed_delete_comment"),
        description: t("community.feed.failed_delete_comment_desc"),
        variant: "destructive",
      });
    }
  };

  const handleAddReply = async (
    postId: string,
    commentId: string,
    reply: string,
    mentionedUserIds: string[] = [],
  ) => {
    // CommentSection handles reply creation and state update directly
    // This handler is intentionally empty to avoid duplicate API calls
    // The reply is already created and managed by CommentSection.tsx
  };

  const handleEditReply = async (
    postId: string,
    commentId: string,
    replyId: string,
    newContent: string,
    mentionedUserIds?: string[],
  ) => {
    try {
      await updateComment(replyId, { content: newContent, mentioned_users: mentionedUserIds });

      // Update local state optimistically
      setPosts(
        posts.map((post) => {
          if (post.id === postId) {
            return {
              ...post,
              comments: post.comments?.map((comment) => {
                if (comment.id === commentId) {
                  return {
                    ...comment,
                    replies: comment.replies.map((reply: any) =>
                      reply.id === replyId
                        ? { ...reply, content: newContent, isEdited: true, editedAt: new Date() }
                        : reply,
                    ),
                  };
                }
                return comment;
              }),
            };
          }
          return post;
        }),
      );

      toast({
        title: t("community.feed.reply_updated"),
        description: t("community.feed.reply_updated_desc"),
      });
    } catch (error) {
      console.error("Error updating reply:", error);
      toast({
        title: t("community.feed.failed_update_reply"),
        description: t("community.feed.failed_update_reply_desc"),
        variant: "destructive",
      });
    }
  };

  const handleDeleteReply = async (postId: string, commentId: string, replyId: string) => {
    try {
      await deleteComment(replyId, "Deleted by user");

      // Update local state optimistically
      setPosts(
        posts.map((post) => {
          if (post.id === postId) {
            return {
              ...post,
              comments: post.comments?.map((comment) => {
                if (comment.id === commentId) {
                  return {
                    ...comment,
                    replies: comment.replies.filter((reply: any) => reply.id !== replyId),
                  };
                }
                return comment;
              }),
            };
          }
          return post;
        }),
      );

      toast({
        title: t("community.feed.reply_deleted"),
        description: t("community.feed.reply_deleted_desc"),
      });
    } catch (error) {
      console.error("Error deleting reply:", error);
      toast({
        title: t("community.feed.failed_delete_reply"),
        description: t("community.feed.failed_delete_reply_desc"),
        variant: "destructive",
      });
    }
  };

  const handleJoinEvent = async (postId: string) => {
    if (!user?.id) return;

    // Find the post - could be a regular event or a shared event
    const post = posts.find((p) => p.id === postId || p.shared_post?.id === postId);
    if (!post) return;

    // Get event details from either regular post or shared post
    const isSharedEvent = !!post.shared_post?.isEvent;
    const eventParticipants = isSharedEvent
      ? post.shared_post?.eventParticipants
      : post.eventParticipants;
    const eventDetails = isSharedEvent ? post.shared_post?.eventDetails : post.eventDetails;

    if (!isSharedEvent && !post.isEvent) return;

    const alreadyRegistered = eventParticipants?.some((p) => p.userId === user.id);
    if (alreadyRegistered) {
      toast({
        title: t("community.feed.already_registered"),
        description: t("community.feed.already_registered_desc"),
        variant: "destructive",
      });
      return;
    }

    if (
      eventDetails?.maxParticipants &&
      (eventParticipants?.length || 0) >= eventDetails.maxParticipants
    ) {
      toast({
        title: t("community.feed.event_is_full"),
        description: t("community.feed.event_is_full_desc"),
        variant: "destructive",
      });
      return;
    }

    try {
      // Call API to register for event
      await registerEvent(postId);

      const newParticipant = {
        userId: user.id,
        userName: user.full_name || t("community.feed.unknown_user"),
        userTitle:
          user.type === "employer" ? t("community.feed.employer") : t("community.feed.job_seeker"),
        userType: user.type,
        organizationId: user.organization_id,
        photoUrl: user.photo_thumbnail_url || user.photo,
        registeredAt: new Date(),
      };

      // Update local state
      setPosts(
        posts.map((p) => {
          // Update regular event post
          if (p.id === postId && p.isEvent) {
            return {
              ...p,
              eventParticipants: [...(p.eventParticipants || []), newParticipant],
            };
          }
          // Update shared event post
          if (p.shared_post?.id === postId && p.shared_post?.isEvent) {
            return {
              ...p,
              shared_post: {
                ...p.shared_post,
                eventParticipants: [...(p.shared_post.eventParticipants || []), newParticipant],
              },
            };
          }
          return p;
        }),
      );

      toast({
        title: t("community.feed.successfully_registered"),
        description: t("community.feed.successfully_registered_desc"),
      });
    } catch (error) {
      console.error("Error joining event:", error);
      toast({
        title: t("community.feed.failed_join_event"),
        description: t("community.feed.failed_join_event_desc"),
        variant: "destructive",
      });
    }
  };

  const handleLeaveEvent = async (postId: string) => {
    if (!user?.id) return;

    try {
      // Call API to unregister from event
      await unregisterEvent(postId);

      // Update local state for BOTH regular and shared events
      setPosts(
        posts.map((p) => {
          // Update regular event post
          if (p.id === postId && p.isEvent) {
            return {
              ...p,
              eventParticipants: (p.eventParticipants || []).filter(
                (participant) => participant.userId !== user.id,
              ),
            };
          }
          // Update shared event post
          if (p.shared_post?.id === postId && p.shared_post?.isEvent) {
            return {
              ...p,
              shared_post: {
                ...p.shared_post,
                eventParticipants: (p.shared_post.eventParticipants || []).filter(
                  (participant) => participant.userId !== user.id,
                ),
              },
            };
          }
          return p;
        }),
      );

      toast({
        title: t("community.feed.successfully_left_event"),
        description: t("community.feed.successfully_left_event_desc"),
      });
    } catch (error) {
      console.error("Error leaving event:", error);
      toast({
        title: t("community.feed.failed_leave_event"),
        description: t("community.feed.failed_leave_event_desc"),
        variant: "destructive",
      });
    }
  };

  const handleCancelEvent = async (postId: string) => {
    try {
      // Call API to cancel event
      await cancelEventAPI(postId);

      // Update local state
      const updateEventStatus = (post: PostData) => {
        if (post.id === postId && post.isEvent) {
          return { ...post, eventStatus: "cancelled" as const };
        }
        return post;
      };
      setPosts((prev) => prev.map(updateEventStatus));
      setUserPosts((prev) => prev.map(updateEventStatus));

      toast({
        title: t("community.feed.event_cancelled"),
        description: t("community.feed.event_cancelled_desc"),
      });
    } catch (error) {
      console.error("Error cancelling event:", error);
      toast({
        title: t("community.feed.failed_cancel_event"),
        description: t("community.feed.failed_cancel_event_desc"),
        variant: "destructive",
      });
    }
  };

  const handleEditEvent = async (
    postId: string,
    eventDetails: {
      title: string;
      date: Date;
      endDate?: Date;
      location: string;
      maxParticipants?: number;
      description?: string;
    },
  ) => {
    try {
      // The event being edited often isn't in the loaded feed — EventsPage keeps
      // its own list, and `posts` is only the current page. Previously this
      // returned early, so the save silently no-opped while EventsPage still
      // toasted success. `updatePost` requires `content`, so fall back to
      // fetching the post rather than bailing.
      const known = posts.find((p) => p.id === postId) ?? userPosts.find((p) => p.id === postId);
      const source = known ?? (await getPostById(postId));
      const isCancelled =
        (known ? known.eventStatus : (source as APIPost).event_status) === "cancelled";

      // Prepare the update payload
      const payload: any = {
        content: source.content, // Keep existing content
        event_status: isCancelled ? "cancelled" : "active",
        event_details: {
          title: eventDetails.title,
          date: eventDetails.date.toISOString(),
          location: eventDetails.location,
          ...(eventDetails.endDate && { end_date: eventDetails.endDate.toISOString() }),
          ...(eventDetails.maxParticipants && { max_participants: eventDetails.maxParticipants }),
          ...(eventDetails.description && { description: eventDetails.description }),
        },
      };

      // Call the API
      await updatePost(postId, payload);

      // Update local state
      setPosts(
        posts.map((p) => {
          if (p.id === postId && p.isEvent) {
            return {
              ...p,
              eventDetails,
              isEdited: true,
              editedAt: new Date(),
            };
          }
          return p;
        }),
      );

      toast({
        title: t("community.feed.event_updated"),
        description: t("community.feed.event_updated_desc"),
      });
    } catch (error) {
      console.error("Error updating event:", error);
      toast({
        title: t("community.feed.failed_update_event"),
        description: t("community.feed.failed_update_event_desc"),
        variant: "destructive",
      });
      // Rethrow so EventsPage keeps its edit form open instead of reporting success.
      throw error;
    }
  };

  return (
    <main data-community-container className="mx-auto w-full max-w-(--breakpoint-2xl)">
      <div className="">
        {/* Mobile Navigation Pills - Visible only on mobile/tablet */}
        <div className="mb-4 xl:hidden">
          <div className="brand-gradient-deep overflow-hidden rounded-2xl border border-white/10 shadow-md">
            <div className="scrollbar-hide flex gap-1 overflow-x-auto p-2">
              {sidebarNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                const badgeCount = "badge" in item ? item.badge : 0;

                return (
                  <Button
                    key={item.id}
                    variant="ghost"
                    size="sm"
                    aria-current={isActive ? "page" : undefined}
                    className={`shrink-0 gap-2 rounded-xl border border-transparent whitespace-nowrap ${
                      isActive
                        ? "bg-white/15 font-semibold text-white hover:bg-white/15 hover:text-white"
                        : "font-medium text-white hover:bg-white/10"
                    }`}
                    onClick={() => handleSectionChange(item.id)}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{item.label}</span>
                    {badgeCount > 0 && (
                      <span className="bg-destructive ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-white">
                        {badgeCount}
                      </span>
                    )}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex gap-4 lg:gap-6">
          {/* Left Sidebar Navigation - Fixed on desktop */}
          <aside className="hidden w-64 shrink-0 xl:block">
            <div className="fixed top-32 w-64">
              <div className="brand-gradient-deep overflow-hidden rounded-2xl border border-white/10 shadow-md">
                <nav aria-label={t("community.feed.title")} className="flex flex-col gap-1 p-3">
                  {sidebarNavItems
                    .filter((item) => item.id !== "latest-jobs") // Hide Latest Jobs on desktop (shown in right sidebar)
                    .map((item) => {
                      const Icon = item.icon;
                      const isActive = activeSection === item.id;
                      const badgeCount = "badge" in item ? item.badge : 0;

                      return (
                        <Button
                          key={item.id}
                          variant="ghost"
                          aria-current={isActive ? "page" : undefined}
                          className={`group relative h-auto w-full justify-start gap-3.5 rounded-xl px-3.5 py-3 text-sm transition-colors ${
                            isActive
                              ? "bg-white/15 font-semibold text-white hover:bg-white/15 hover:text-white"
                              : "font-medium text-white hover:bg-white/10"
                          }`}
                          onClick={() => handleSectionChange(item.id)}
                        >
                          {isActive && (
                            <span className="absolute top-1.5 bottom-1.5 -left-3 w-1 rounded-r-full bg-white" />
                          )}
                          <Icon className="h-5 w-5 shrink-0" />
                          <span className="truncate">{item.label}</span>
                          {badgeCount > 0 && (
                            <span className="bg-destructive ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-white">
                              {badgeCount}
                            </span>
                          )}
                        </Button>
                      );
                    })}
                </nav>
              </div>
            </div>
          </aside>

          {/* Main Content Area */}
          <div className="w-full min-w-0 flex-1 space-y-4 sm:space-y-6 xl:w-auto">
            <div className="space-y-1 md:hidden">
              <h1 className="text-foreground text-xl font-bold tracking-tight sm:text-2xl">
                <span className="gradient-text">{t("community.feed.title")}</span>
              </h1>
              <p className="text-muted-foreground text-sm">{t("community.feed.description")}</p>
            </div>

            {activeSection === "feed" && (
              <>
                <CreatePost onPostCreated={handlePostCreated} />

                <div className="mt-0 space-y-6">
                  {isLoading ? (
                    // Show skeleton loaders during initial load
                    <>
                      {Array.from({ length: 3 }).map((_, index) => (
                        <PostSkeleton key={`skeleton-${index}`} />
                      ))}
                    </>
                  ) : error ? (
                    <Card className="border-border">
                      <div className="flex flex-col items-center gap-3 py-14 text-center">
                        <div className="bg-destructive/10 text-destructive flex h-12 w-12 items-center justify-center rounded-full">
                          <Rss className="h-6 w-6" />
                        </div>
                        <div className="space-y-1">
                          <p className="text-destructive text-base font-semibold">
                            {t("community.feed.error_loading_posts")}
                          </p>
                          <p className="text-muted-foreground text-sm">{error}</p>
                        </div>
                        <Button
                          onClick={() => {
                            setCurrentPage(1);
                            setHasMore(true);
                            fetchPostsData(1, true, true);
                          }}
                          variant="outline"
                        >
                          {t("community.feed.retry")}
                        </Button>
                      </div>
                    </Card>
                  ) : posts.length === 0 ? (
                    <Card className="border-border">
                      <div className="flex flex-col items-center gap-3 py-14 text-center">
                        <div className="bg-muted text-muted-foreground flex h-12 w-12 items-center justify-center rounded-full">
                          <Rss className="h-6 w-6" />
                        </div>
                        <div className="space-y-1">
                          <p className="text-foreground text-base font-semibold">
                            {t("community.feed.no_posts_yet")}
                          </p>
                          <p className="text-muted-foreground text-sm">
                            {t("community.feed.be_first_to_share")}
                          </p>
                        </div>
                      </div>
                    </Card>
                  ) : (
                    <>
                      {posts.map((post) => (
                        <Post
                          key={post.id}
                          post={post}
                          currentUserId={user?.id}
                          onLike={handleLike}
                          onComment={handleComment}
                          onShare={handleShare}
                          onEdit={handleEdit}
                          onDelete={handleDelete}
                          onEditComment={handleEditComment}
                          onDeleteComment={handleDeleteComment}
                          onAddReply={handleAddReply}
                          onEditReply={handleEditReply}
                          onDeleteReply={handleDeleteReply}
                          onJoinEvent={handleJoinEvent}
                          onLeaveEvent={handleLeaveEvent}
                          onCancelEvent={handleCancelEvent}
                          onEditEvent={handleEditEvent}
                        />
                      ))}

                      {/* Infinite scroll trigger element */}
                      {hasMore && (
                        <div ref={loadMoreRef} className="py-4">
                          {isLoadingMore && (
                            <div className="space-y-6">
                              {Array.from({ length: 2 }).map((_, index) => (
                                <PostSkeleton key={`loading-more-${index}`} />
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* End of feed indicator */}
                      {!hasMore && posts.length > 0 && (
                        <div className="py-8 text-center">
                          <p className="text-muted-foreground text-sm">
                            {t("community.feed.end_of_feed")}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}

            {activeSection === "my-posts" && (
              <>
                <div className="space-y-1">
                  <h2 className="text-foreground text-lg font-bold tracking-tight sm:text-xl">
                    {t("community.feed.my_posts_title")}
                  </h2>
                  <p className="text-muted-foreground text-sm">
                    {t("community.feed.my_posts_description")}
                  </p>
                </div>

                <div className="space-y-6">
                  {isLoadingUserPosts ? (
                    <>
                      {Array.from({ length: 3 }).map((_, index) => (
                        <PostSkeleton key={`user-skeleton-${index}`} />
                      ))}
                    </>
                  ) : userPosts.length > 0 ? (
                    userPosts.map((post) => (
                      <Post
                        key={post.id}
                        post={post}
                        currentUserId={user?.id}
                        onLike={handleLike}
                        onComment={handleComment}
                        onShare={handleShare}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onEditComment={handleEditComment}
                        onDeleteComment={handleDeleteComment}
                        onAddReply={handleAddReply}
                        onEditReply={handleEditReply}
                        onDeleteReply={handleDeleteReply}
                        onJoinEvent={handleJoinEvent}
                        onLeaveEvent={handleLeaveEvent}
                        onCancelEvent={handleCancelEvent}
                        onEditEvent={handleEditEvent}
                      />
                    ))
                  ) : (
                    <Card className="border-border">
                      <div className="flex flex-col items-center gap-3 py-14 text-center">
                        <div className="bg-muted text-muted-foreground flex h-12 w-12 items-center justify-center rounded-full">
                          <FileText className="h-6 w-6" />
                        </div>
                        <div className="space-y-1">
                          <p className="text-foreground text-base font-semibold">
                            {t("community.feed.no_posts_yet")}
                          </p>
                          <p className="text-muted-foreground text-sm">
                            {t("community.feed.start_sharing")}
                          </p>
                        </div>
                      </div>
                    </Card>
                  )}
                </div>
              </>
            )}

            {activeSection === "events" && (
              <EventsPage
                key={refreshKey}
                feedPosts={posts}
                onEditEvent={handleEditEvent}
                onCancelEvent={handleCancelEvent}
                onLeaveEvent={handleLeaveEvent}
                onJoinEvent={handleJoinEvent}
              />
            )}

            {activeSection === "support" && <SupportPage />}

            {activeSection === "latest-jobs" && (
              <div key={refreshKey} className="space-y-4">
                <LatestJobs />
              </div>
            )}
          </div>

          {/* Right Sidebar - Job Posts */}
          {user?.type === "job_seeker" && (
            <aside className="hidden w-80 shrink-0 xl:block">
              <div className="">
                <LatestJobs />
              </div>
            </aside>
          )}
        </div>
      </div>
    </main>
  );
}
